import { IdxStep, TokenParams } from '@mgmresorts/mgm-auth-js-sdk';
import React, { useState, useEffect, useCallback, useRef } from 'react';

import { OktaUser } from '../../../../@types/okta-user';
import { SMSVerification } from '../../../components/ui/SMSVerification';
import { useAppRedirect } from '../../../hooks/useAppRedirect';
import { useContent } from '../../../hooks/useContent';
import { useCountdown } from '../../../hooks/useCountdown';
import { useInput } from '../../../hooks/useInput';
import { useLoadingLabel } from '../../../hooks/useLoadingLabel';
import { useEvent } from '../../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../../utils/analytics/usePageLoadEvent';
import { useAuthContext } from '../../../utils/auth/AuthProvider';
import { ClientId } from '../../../utils/constants';
import { getFeaturesByClientId } from '../../../utils/getFeaturesByClientId';
import { getThirdPartyScopesByClientId } from '../../../utils/getScopes';
import { linkAccountToPartner } from '../../../utils/linkAccountToPartner';
import { getPersistedParameters } from '../../../utils/persistParameterContext';
import { getUrlParams } from '../../../utils/url';
import { useUserAccount } from '../../../utils/userAccountContext';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function SMSVerificationSignin({ type }: { type: 'enrollment' | 'authentication' }) {
  const redirectToApp = useAppRedirect();
  const { getContentByPath } = useContent();
  const trackEvent = useEvent();
  const eventNameError = getContentByPath<string>('mfa.analytics.error.eventName');
  const trackSuffixError = getContentByPath<string>('mfa.analytics.error.trackSuffix');
  usePageLoadEvent('identity', type === 'enrollment' ? '2fa setup: sms auth code' : 'sign in: sms auth');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setLoadingState] = useState(false);
  const submitCTA = useLoadingLabel(isLoading, 'SMSVerificationSignin.submit');
  const { authClient } = useAuthContext();
  const {
    bind: bindVerificationCode,
    value: verificationCode,
    reset: resetVerificationCode,
  } = useInput('', 'verificationCode');
  const [inputError, setInputError] = useState<string>('');
  const [touched, setTouched] = useState(false);
  const [codeResentSuccessfully, setCodeResentSuccessfully] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const { phoneNumber, setUserPhoneNumber } = useUserAccount();
  const phoneOtpHandler = authClient?.idx?.stepHandlers?.[IdxStep.AuthPhone];
  const confirmPhoneOtpHandler = authClient?.idx?.stepHandlers?.[IdxStep.AuthPhoneConfirm];

  const countDown = useCountdown(phoneOtpHandler?.resendRateLimitSeconds);
  const smsSentRef = useRef(false);

  console.log('SMSVerificationSignin rendered with phoneOtpHandler:', phoneOtpHandler);
  // Capture phone number early to prevent it from becoming undefined
  useEffect(() => {
    if (phoneOtpHandler?.phoneNumber && !phoneNumber) {
      setUserPhoneNumber(phoneOtpHandler?.phoneNumber);
    }
  }, [phoneNumber, setUserPhoneNumber, phoneOtpHandler]);

  useEffect(() => {
    if (countDown.startCountdown) {
      countDown.startCountdown();
    }
  }, [countDown]);

  useEffect(() => {
    // Don't run if handlers aren't available
    if (!confirmPhoneOtpHandler) return;

    // Prevent duplicate SMS sends (React 18 Strict Mode calls useEffect twice)
    if (smsSentRef.current) return;
    smsSentRef.current = true;

    const sendCodeToPhone = async () => {
      try {
        await confirmPhoneOtpHandler?.confirmPhoneAuth();
        setOpenAlert(true);
        setCodeResentSuccessfully(true);

        const message = getContentByPath('mfa.sms.codeResentSuccess') as string;
        await trackEvent(
          'identity – unified MFA notification',
          message,
          'unifiedmfanotification',
          { notificationDisplayed: message },
          true,
        );
      } catch (err) {
        console.error('Error during sms authentication setup:', err);
        setErrorMessage(getContentByPath('mfa.sms.codeResentFail'));
        trackEvent(eventNameError, getContentByPath('mfa.sms.codeResentFail'), trackSuffixError, {}, true);
        setOpenAlert(true);
      }
    };

    sendCodeToPhone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateCode = useCallback(async () => {
    setTouched(true);
    if (verificationCode.length === 0) {
      setInputError(getContentByPath('mfa.sms.fields.verificationCode.required'));
      trackEvent(
        eventNameError,
        getContentByPath('mfa.sms.fields.verificationCode.required'),
        trackSuffixError,
        {},
        true,
      );
      return false;
    }
    if (verificationCode.length > 0 && verificationCode.length < 6) {
      setInputError(getContentByPath('mfa.sms.fields.verificationCode.invalid'));
      trackEvent(
        eventNameError,
        getContentByPath('mfa.sms.fields.verificationCode.invalid'),
        trackSuffixError,
        {},
        true,
      );
      return false;
    }
    setInputError('');
    return true;
  }, [verificationCode, getContentByPath, trackEvent, eventNameError, trackSuffixError]);
  const handleVerificationCodeBlur = () => {
    validateCode();
  };

  const handleVerificationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/[^0-9]/g, '');
    const syntheticEvent = {
      ...e,
      target: { ...e.target, value: numericValue },
    };

    bindVerificationCode.onChange(syntheticEvent);

    // Clear error if user is typing and field becomes valid
    if (touched) {
      if (numericValue.length === 6) {
        setInputError('');
      } else if (numericValue.length > 0 && numericValue.length < 6) {
        setInputError(getContentByPath('mfa.sms.fields.verificationCode.invalid') || 'Invalid code');
        trackEvent(
          eventNameError,
          getContentByPath('mfa.sms.fields.verificationCode.invalid') || 'Invalid code',
          trackSuffixError,
          {},
          true,
        );
      }
    }
  };

  const handleSubmit = useCallback(async () => {
    if (!validateCode()) {
      return;
    }
    if (!phoneOtpHandler) {
      console.error('SMS OTP handler not available');
      return;
    }

    await trackEvent('identity – unified MFA CTAs', 'SMS Authentication Code - Submit', 'unifiedmfactas', {}, true);

    setLoadingState(true);
    try {
      await phoneOtpHandler.phoneAuth(verificationCode);
      const tokens = authClient.idx.idxTransaction?.tokens;
      if (tokens) {
        authClient.getAuthClient().tokenManager.setTokens(tokens);
        try {
          const oktaAuth = authClient.getAuthClient();
          if (tokens.idToken) {
            const sessionExists = await oktaAuth.session.exists();

            if (!sessionExists) {
              try {
                await oktaAuth.session.refresh();
              } catch (refreshErr) {
                console.warn('Session refresh failed:', refreshErr);
              }
            } else {
              console.log('✓ Okta session exists!');
            }
          }
        } catch (sessionErr) {
          console.error('Error creating Okta session:', sessionErr);
          // Continue anyway - tokens are set, might still work
        }
      } else {
        throw new Error('No tokens available after MFA verification');
      }

      // Get persisted parameters to check client configuration
      const params = getPersistedParameters();
      const { client_id } = params;
      const { isAllowOktaSignInWithRedirect } = getFeaturesByClientId(client_id as ClientId);

      if (isAllowOktaSignInWithRedirect) {
        const urlParams = getUrlParams();
        const options: TokenParams = {
          redirectUri: urlParams.redirectUri,
          codeChallenge: urlParams.codeChallenge,
          scopes: getThirdPartyScopesByClientId(urlParams.clientId as ClientId),
          state: urlParams.state,
        };
        await authClient.getAuthClient().signInWithRedirect(options);
        return;
      }
      await new Promise(resolve => setTimeout(resolve, 100));

      // Verify tokens are actually in the token manager
      const accessToken = await authClient.getAuthClient().tokenManager.get('accessToken');
      const idToken = await authClient.getAuthClient().tokenManager.get('idToken');

      if (accessToken && idToken) {
        await trackEvent(
          'identity – sign in - credentials submit success',
          'sign in credentials submit success',
          'signinsubmitsuccess',
          { signInState: 'success' },
          true,
        );

        // Handle partner linking if needed
        if (params.partner && params.tokenKey) {
          try {
            const activeSessionObj = await authClient.hasActiveMemberSession();
            if (activeSessionObj?.activeMemberSession && activeSessionObj?.session?.user) {
              const user = (await activeSessionObj.session.user()) as OktaUser;
              await linkAccountToPartner(params.partner as string, params.tokenKey as string, user.profile.mlifeNumber);
            }
          } catch (err) {
            console.warn('Partner linking failed:', err);
          }
        }
        redirectToApp();
      } else {
        console.error('Tokens not found in token manager after setting');
        throw new Error('Failed to persist tokens in token manager');
      }
    } catch (err) {
      setCodeResentSuccessfully(false);
      console.error('Error during sms authentication:', err);

      setInputError(getContentByPath('mfa.email.invalidCode'));
      trackEvent(eventNameError, getContentByPath('mfa.email.invalidCode'), trackSuffixError, {}, true);
      setOpenAlert(true);
      setLoadingState(false);
    }
  }, [
    redirectToApp,
    authClient,
    phoneOtpHandler,
    verificationCode,
    getContentByPath,
    validateCode,
    trackEvent,
    eventNameError,
    trackSuffixError,
  ]);

  const handleResendCode = async () => {
    if (!phoneOtpHandler) return;

    if (!countDown.isRunning && phoneOtpHandler.canResendCode) {
      try {
        await trackEvent('identity – unified MFA CTAs', 'Resend Authentication Code - SMS', 'unifiedmfactas', {}, true);

        await phoneOtpHandler.resendCode();
        setOpenAlert(true);
        setCodeResentSuccessfully(true);
        setErrorMessage('');
        setInputError('');
        setTouched(false);
        resetVerificationCode();
        countDown.startCountdown();

        const message = getContentByPath('mfa.sms.codeResentSuccess') as string;
        await trackEvent(
          'identity – unified MFA notification',
          message,
          'unifiedmfanotification',
          { notificationDisplayed: message },
          true,
        );
      } catch (err) {
        setCodeResentSuccessfully(false);
        setOpenAlert(true);
        setErrorMessage(getContentByPath('mfa.sms.codeResentFail'));
        trackEvent(eventNameError, getContentByPath('mfa.sms.codeResentFail'), trackSuffixError, {}, true);
      }
    } else {
      setCodeResentSuccessfully(false);
      setOpenAlert(true);
      setErrorMessage(getContentByPath('mfa.sms.codeResentFail'));
      trackEvent(eventNameError, getContentByPath('mfa.sms.codeResentFail'), trackSuffixError, {}, true);
    }
  };
  const maskPhoneNumber = phone => {
    const cleaned = phone.replace(/[^\d+]/g, ''); // remove spaces, dashes etc.

    const match = cleaned.match(/^(\+\d{1,3})?(.*?)(\d{4})$/);

    if (!match) return phone;

    const countryCode = match[1] || '';
    const last4 = match[3];

    return `${countryCode}... ...${last4}`;
  };

  const onCloseAlert = () => {
    setOpenAlert(false);
  };

  return (
    <div data-testid='route-/mfa/auth/sms'>
      <SMSVerification
        onCloseAlert={onCloseAlert}
        phoneNumber={phoneNumber ? `${maskPhoneNumber(phoneNumber)}` : 'unknown'}
        verificationCode={verificationCode}
        onVerificationCodeChange={handleVerificationCodeChange}
        onVerificationCodeBlur={handleVerificationCodeBlur}
        onSubmit={handleSubmit}
        onResend={handleResendCode}
        isLoading={isLoading}
        inputError={!!inputError}
        inputHint={inputError}
        isCountdownRunning={countDown.isRunning}
        secondsLeft={countDown.secondsLeft}
        submitButtonLabel={submitCTA || getContentByPath('mfa.sms.submit')}
        codeResentSuccessfully={codeResentSuccessfully}
        errorMessage={errorMessage}
        openAlert={openAlert}
        type={type}
      />
    </div>
  );
}

SMSVerificationSignin.displayName = 'SMSVerificationSignin';
